import React from "react";
import { Link } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";import Seo, { breadcrumbSchema, faqSchema } from "../components/Seo";
import { Reveal, Eyebrow, PageHero, BtnPrimary, BtnGhost, FaqList, CtaBand, Crumbs } from "../components/Shared";
import { GoogleReviewsFeed } from "../components/GoogleReviews";
import { PRICING, IMG, GOLD_STANDARD, chalkitLink } from "../data/mock";
import { QuickAnswer } from "../components/QuickAnswer";

const AGGREGATE_OFFER = {
  "@context": "https://schema.org",
  "@type": "AggregateOffer",
  "@id": "https://bostonirongrittraining.com/pricing#memberships",
  name: "Boston Iron Grit Memberships",
  url: "https://bostonirongrittraining.com/pricing",
  priceCurrency: "USD",
  lowPrice: "35",
  highPrice: "6800",
  offerCount: 17,
  offeredBy: { "@id": "https://bostonirongrittraining.com/#business" },
  offers: [
    { "@type": "Offer", name: "Platinum Membership", price: "275", priceCurrency: "USD", description: "Unlimited CrossFit classes, Olympic Weightlifting & HYROX Club, Live B.I.G. 365 wellness program included, open gym access, free parking & showers.", availability: "https://schema.org/InStock", priceSpecification: { "@type": "UnitPriceSpecification", price: "275", priceCurrency: "USD", billingIncrement: 1, unitCode: "MON" } },
    { "@type": "Offer", name: "3x / Week", price: "225", priceCurrency: "USD", description: "Three CrossFit classes per week, any class time on the schedule, beginner-friendly scaling.", priceSpecification: { "@type": "UnitPriceSpecification", price: "225", priceCurrency: "USD", billingIncrement: 1, unitCode: "MON" } },
    { "@type": "Offer", name: "Live B.I.G. 365", price: "100", priceCurrency: "USD", description: "8-daily-habits wellness program. Included with the Platinum Membership.", priceSpecification: { "@type": "UnitPriceSpecification", price: "100", priceCurrency: "USD", billingIncrement: 1, unitCode: "MON" } },
    { "@type": "Offer", name: "Heroes Platinum", price: "225", priceCurrency: "USD", description: "Full Platinum membership with unlimited classes for veterans, first responders and their family members." },
    { "@type": "Offer", name: "10 Class Pack", price: "300", priceCurrency: "USD", description: "10 classes, 3-month expiration." },
    { "@type": "Offer", name: "30 Class Pack", price: "750", priceCurrency: "USD", description: "30 classes, 8-month expiration." },
    { "@type": "Offer", name: "52 Class Pack", price: "1040", priceCurrency: "USD", description: "52 classes, 12-month expiration." },
    { "@type": "Offer", name: "5 Private Sessions", price: "750", priceCurrency: "USD" },
    { "@type": "Offer", name: "10 Private Sessions", price: "1250", priceCurrency: "USD" },
    { "@type": "Offer", name: "Fundamentals — 5 Private Sessions", price: "625", priceCurrency: "USD", description: "Beginner on-ramp. Includes InBody analysis and assessment session." },
    { "@type": "Offer", name: "HYROX Add-On", price: "50", priceCurrency: "USD", priceSpecification: { "@type": "UnitPriceSpecification", price: "50", priceCurrency: "USD", billingIncrement: 1, unitCode: "MON" } },
    { "@type": "Offer", name: "HYROX Race Training Punch Card", price: "600", priceCurrency: "USD", description: "24 sessions." },
    { "@type": "Offer", name: "Drop-In (Visiting Athlete)", price: "35", priceCurrency: "USD", description: "Single class for experienced CrossFit athletes. Book ahead." },
    { "@type": "Offer", name: "Drop-In Week Pass", price: "75", priceCurrency: "USD" },
    { "@type": "Offer", name: "Gold Standard Tier 01 — The Foundation", price: "1985", priceCurrency: "USD", description: "3-month program." },
    { "@type": "Offer", name: "Gold Standard Tier 02 — The Ascension", price: "2525", priceCurrency: "USD", description: "3-month program." },
    { "@type": "Offer", name: "Gold Standard Tier 03 — The Elite Performance", price: "6800", priceCurrency: "USD", description: "6-month program." },
  ],
};

const pricingFaqs = [
  {
    q: "How much does CrossFit cost in West Roxbury?",
    a: "At Boston Iron Grit, the Platinum Membership with unlimited CrossFit is $275/month and a 3x/week membership is $225/month — both month-to-month with no contracts. Nationally, coached CrossFit memberships typically run $150–$250+/month depending on city and class caps; ours include the Live B.I.G. 365 wellness program, open gym access, free parking and showers.",
  },
  { q: "Is the Platinum Membership the same as the old Unlimited membership?", a: "Yes. Our unlimited membership is now called the Platinum Membership — $275/month for unlimited CrossFit classes, Olympic weightlifting, HYROX Club, open gym access and the Live B.I.G. 365 wellness program included. Same price, same access, clearer name." },
  { q: "How much does CrossFit cost in Boston?", a: "At Boston Iron Grit, the Platinum Membership is $275/month (including the Live B.I.G. 365 wellness program) and 3x/week is $225/month. Every membership includes coaching and programming — not just building access." },
  { q: "Are there contracts or initiation fees?", a: "No. All memberships are month-to-month with no initiation fees or long-term contracts." },
  { q: "What's the best option for beginners?", a: "Start with a free goal review, then the Fundamentals program — five private sessions for $625, including an InBody analysis and assessment. After that, most beginners choose the 3x/Week membership." },
  { q: "Do you offer any discounts?", a: "Yes — veterans, first responders and their family members get the Platinum Membership for $225/month. Punch cards also include Wellness Program discounts." },
  { q: "Can I pause or cancel my membership?", a: "Yes — holds and cancellations are handled directly through ChalkIt Pro or by talking to us. No hoops." },
  { q: "Do you offer drop-ins for visiting athletes?", a: "Yes — $35 per class for experienced CrossFit athletes. Book ahead through the drop-in page." },
];

export default function Pricing() {
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Pricing", path: "/pricing" },
  ];

  return (
    <>
      <Seo
        title="Membership & Pricing in West Roxbury | Boston Iron Grit"
        description="CrossFit membership pricing in West Roxbury: Unlimited $275/mo, 3x/week $225/mo, class packs, personal training and $35 drop-ins. No contracts."
        path="/pricing"
        schemas={[breadcrumbSchema(crumbs), faqSchema(pricingFaqs), AGGREGATE_OFFER]}
      />
      <PageHero
        label="Memberships & Pricing"
        title={<>Honest pricing.<br /><span className="text-neutral-500">Real coaching included.</span></>}
        sub="Every membership includes expert coaching and daily programming. Month-to-month, no initiation fees, no contracts. Checkout is handled securely by ChalkIt Pro."
        image={IMG.kettlebell}
      />

      <section className="bg-[#0A0A0B] pb-6">
        <div className="max-w-7xl mx-auto px-6">
          <Crumbs items={crumbs} />
        </div>
      </section>

      {/* Plans */}
      <section className="bg-[#0A0A0B] py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PRICING.plans.map((p, i) => {
              const feat = p.id === PRICING.featured;
              return (
                <Reveal key={p.id} delay={i * 80}>
                  <div
                    className={`relative flex flex-col h-full p-7 transition-all duration-300 hover:translate-y-[-4px] ${
                      feat
                        ? "bg-[#141416] border-2 border-[#9D0423] shadow-[0_20px_60px_rgba(157,4,35,0.22)]"
                        : "bg-[#121214] border border-white/8 hover:border-white/20"
                    }`}
                  >
                    {feat && (
                      <div className="absolute -top-3.5 left-6 flex items-center gap-1.5 bg-[#9D0423] text-white font-mono text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">
                        <Sparkles size={11} /> Most Popular
                      </div>
                    )}
                    <h2 className="font-heading font-bold uppercase text-xl text-white tracking-tight">{p.name}</h2>
                    <div className="mt-4 flex items-baseline gap-1.5">
                      <span className="font-heading font-extrabold text-5xl text-white">{p.price}</span>
                      <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-neutral-500">{p.per}</span>
                    </div>
                    <p className="mt-4 text-[13px] text-neutral-400 leading-relaxed">{p.blurb}</p>
                    {p.learnMore && (
                      <Link to={p.learnMore} className="mt-2 inline-block font-mono text-[11px] uppercase tracking-[0.16em] text-[#E56A79] hover:text-white transition-colors">
                        Explore the program →
                      </Link>
                    )}
                    <ul className="mt-6 space-y-2.5 flex-1">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2.5">
                          <Check size={15} className="text-[#9D0423] mt-0.5 shrink-0" />
                          <span className="text-[13px] text-neutral-300">{f}</span>
                        </li>
                      ))}
                    </ul>
                    {p.id === "heroes" ? (
                      <Link
                        to="/get-started"
                        data-testid="pricing-plan-cta-heroes"
                        className={`mt-7 inline-flex items-center justify-center font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-5 py-3.5 transition-all duration-200 ${
                          feat
                            ? "bg-[#9D0423] hover:bg-[#7E031C] text-white hover:translate-y-[-1px]"
                            : "border border-white/20 hover:border-white/60 text-white"
                        }`}
                      >
                        {p.cta}
                      </Link>
                    ) : (
                    <a
                      href={chalkitLink(p.id)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`pricing-plan-cta-${p.id}`}
                      className={`mt-7 inline-flex items-center justify-center font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-5 py-3.5 transition-all duration-200 ${
                        feat
                          ? "bg-[#9D0423] hover:bg-[#7E031C] text-white hover:translate-y-[-1px]"
                          : "border border-white/20 hover:border-white/60 text-white"
                      }`}
                    >
                      {p.cta}
                    </a>
                    )}
                  </div>
                </Reveal>
              );
            })}
          </div>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.14em] text-neutral-500">{PRICING.note}</p>

          {/* Member reviews — live Google Business Profile feed */}
          <div className="mt-14" data-testid="pricing-reviews">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-400">What members say on Google</p>
            <GoogleReviewsFeed className="mt-6" />
          </div>
        </div>
      </section>

      {/* Punch Cards */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>3 Punch Card Options</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight">
              10, 30 or 52 classes
            </h2>
            <p className="mt-4 text-neutral-400 text-[15px] max-w-2xl">{PRICING.punchCards.blurb}</p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            {PRICING.punchCards.options.map((p, i) => (
              <Reveal key={p.classes} delay={i * 80}>
                <div className="p-7 bg-[#121214] border border-white/8 hover:border-[#9D0423]/50 transition-all duration-300 hover:translate-y-[-4px] h-full">
                  <div className="font-heading font-extrabold text-5xl text-white">{p.classes}<span className="text-[#9D0423]">.</span></div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500 mt-1">Classes</p>
                  <div className="mt-5 flex items-baseline gap-2">
                    <span className="font-heading font-extrabold text-3xl text-[#E56A79]">{p.price}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-neutral-500">{p.exp}</span>
                  </div>
                  <ul className="mt-5 space-y-2">
                    {["Wellness Program discounts", "Flexibility & accountability", "Any class on the schedule"].map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <Check size={14} className="text-[#9D0423] mt-0.5 shrink-0" />
                        <span className="text-[13px] text-neutral-300">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <a href={chalkitLink(`punch${p.classes}`)} target="_blank" rel="noopener noreferrer" data-testid={`pricing-punch-cta-${p.classes}`} className="mt-6 inline-flex items-center justify-center w-full border border-white/20 hover:border-white/60 text-white font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-5 py-3.5 transition-colors">
                    Buy {p.classes} Classes
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Private Training */}
      <section className="bg-[#0A0A0B] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Private Training</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight">
              One coach. One athlete.
            </h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            {PRICING.privateTraining.map((p, i) => (
              <Reveal key={p.fundamentals ? "fundamentals" : p.sessions} delay={i * 80}>
                <div className={`p-7 h-full transition-all duration-300 hover:translate-y-[-4px] ${i === 1 ? "bg-[#141416] border-2 border-[#9D0423]" : "bg-[#121214] border border-white/8 hover:border-white/20"}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">{p.fundamentals ? p.name : `${p.sessions} Private Sessions`}</p>
                  {p.fundamentals && <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#E56A79]">{p.sub}</p>}
                  <div className="mt-3 font-heading font-extrabold text-5xl text-white">{p.price}</div>
                  <p className="mt-3 text-[13px] text-neutral-400">{p.note}</p>
                  <a href={chalkitLink(p.fundamentals ? "fundamentals" : `pt${p.sessions}`)} target="_blank" rel="noopener noreferrer" data-testid={p.fundamentals ? "pricing-pt-cta-fundamentals" : `pricing-pt-cta-${p.sessions}`} className={`mt-6 inline-flex items-center justify-center w-full font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-5 py-3.5 transition-colors ${i === 1 ? "bg-[#9D0423] hover:bg-[#7E031C] text-white" : "border border-white/20 hover:border-white/60 text-white"}`}>
                    {p.fundamentals ? "Start Fundamentals" : `Book ${p.sessions} Sessions`}
                  </a>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 text-[13px] text-neutral-400" data-testid="pricing-fundamentals-caption">
            New to us? Fundamentals is five coached sessions for <strong className="text-white">$625</strong> — $125 less than five standard private sessions.
          </p>
        </div>
      </section>

      {/* Fundamentals Program */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Fundamentals Program</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight">
              Your beginner on-ramp
            </h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 gap-5 max-w-4xl">
            <Reveal>
              <div className="p-8 h-full bg-[#9D0423]">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/80">{PRICING.fundamentals[0].name}</p>
                <div className="mt-3 font-heading font-extrabold text-5xl text-white">{PRICING.fundamentals[0].price}</div>
                <p className="mt-3 text-[13px] leading-relaxed text-white/85">{PRICING.fundamentals[0].note}</p>
                <Link to="/get-started" className="mt-6 inline-flex items-center justify-center w-full bg-[#0A0A0B] hover:bg-black text-white font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-5 py-3.5 transition-colors">
                  Book Free Goal Review
                </Link>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div className="p-8 h-full bg-[#121214] border border-white/8">
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">Then: Fundamentals</p>
                <p className="mt-3 text-[13px] leading-relaxed text-neutral-400">
                  After your free goal review, most beginners start with the Fundamentals program — five private sessions for $625 (see Private Training above), covering the assessment, the Olympic lifts and gymnastics basics.
                </p>
                <Link to="/classes/fundamentals" data-testid="pricing-fundamentals-learn-link" className="mt-6 inline-flex items-center justify-center w-full border border-white/20 hover:border-white/60 text-white font-mono font-semibold text-[12px] uppercase tracking-[0.14em] px-5 py-3.5 transition-colors">
                  Learn About Fundamentals
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Extras */}
      <section className="bg-[#0A0A0B] py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Add-Ons & Extras</Eyebrow>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight mb-10">
              Beyond the membership
            </h2>
          </Reveal>
          <div className="grid gap-px bg-white/5 border border-white/5">
            {PRICING.extras.map((e) => {
              const inner = (
                <>
                  <div>
                    <h3 className="font-heading font-bold uppercase text-base text-white tracking-tight">{e.name}</h3>
                    <p className="mt-1 text-[13px] text-neutral-500">{e.note}</p>
                  </div>
                  <div className="font-heading font-extrabold text-xl text-[#E56A79]">{e.price}</div>
                </>
              );
              return e.link ? (
                <Link key={e.name} to={e.link} data-testid="pricing-extra-dropin-link" className="grid sm:grid-cols-[1fr_auto] items-center gap-4 bg-[#0D0D0F] hover:bg-[#121214] transition-colors px-6 py-5">
                  {inner}
                </Link>
              ) : (
                <div key={e.name} className="grid sm:grid-cols-[1fr_auto] items-center gap-4 bg-[#0D0D0F] hover:bg-[#121214] transition-colors px-6 py-5">
                  {inner}
                </div>
              );
            })}
          </div>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <BtnPrimary to="/drop-in">See Drop-In Options</BtnPrimary>
            <BtnGhost to="/get-started">Not Sure? Free Goal Review</BtnGhost>
          </div>
        </div>
      </section>

      {/* Gold Standard Programs */}
      <section className="bg-[#0D0D0F] border-y border-white/5 py-16 lg:py-20" data-testid="pricing-gold-standard">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] mb-4 text-[#D4AF37]">// {GOLD_STANDARD.tagline}</p>
            <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight">
              Gold Standard Programs
            </h2>
            <p className="mt-4 text-neutral-400 text-[15px] max-w-2xl">{GOLD_STANDARD.blurb}</p>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            {GOLD_STANDARD.tiers.map((t, i) => (
              <Reveal key={t.name} delay={i * 80}>
                <div className={`p-7 h-full transition-all duration-300 hover:translate-y-[-4px] ${t.featured ? "bg-[#141416] border-2 border-[#D4AF37]" : "bg-[#121214] border border-white/8 hover:border-[#D4AF37]/40"}`}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-neutral-500">Tier {String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-3 font-heading font-extrabold uppercase text-2xl text-white tracking-tight">{t.name}<span className="text-[#D4AF37]">.</span></h3>
                  <div className="mt-3 flex items-baseline gap-2 flex-wrap">
                    <span className="font-heading font-extrabold text-3xl text-white">{t.price}</span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#E8C766]">{t.save} · {t.term}</span>
                  </div>
                  <p className="mt-3 text-[13px] text-neutral-400 leading-relaxed">{t.detail}</p>
                  <Link to="/gold-standard" className="mt-5 inline-block font-mono text-[11px] uppercase tracking-[0.16em] text-[#E8C766] hover:text-white transition-colors">
                    Explore the tier →
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.14em] text-neutral-500">{GOLD_STANDARD.note}</p>
          <p className="mt-3 text-[13px] text-neutral-400 max-w-3xl">
            <span className="text-[#E8C766] font-semibold">{GOLD_STANDARD.platinum.name} {GOLD_STANDARD.platinum.price}{GOLD_STANDARD.platinum.per}</span> — {GOLD_STANDARD.platinum.detail}
          </p>
          <div className="mt-8">
            <BtnGhost to="/get-started">Get Priced at Your Free Goal Review</BtnGhost>
          </div>
        </div>
      </section>

      {/* How much does CrossFit cost */}
      <section className="bg-[#0A0A0B] py-16 lg:py-20" data-testid="pricing-cost-section">
        <div className="max-w-4xl mx-auto px-6">
          <Eyebrow>The Honest Answer</Eyebrow>
          <h2 className="font-heading font-extrabold uppercase text-3xl lg:text-4xl text-white tracking-tight">
            How much does CrossFit cost?
          </h2>
          <p className="mt-6 text-[15px] text-neutral-300 leading-[1.85]">
            Across the U.S., a coached CrossFit membership typically costs between $150 and $250+ per month — more in major cities.
            The price reflects what you're actually buying: a credentialed coach running every class, programming that changes daily,
            and a class size small enough that your squat gets watched and corrected.
          </p>
          <p className="mt-4 text-[15px] text-neutral-300 leading-[1.85]">
            At Boston Iron Grit, the Platinum Membership gives you unlimited classes for <strong className="text-white">$275/month</strong> and a 3x/week membership is{" "}
            <strong className="text-white">$225/month</strong> — and Platinum includes the Live B.I.G. 365 wellness program ($100/month on
            its own), open gym access, free parking and showers. Veterans and first responders get Platinum for $225/month. Punch cards
            start at $300 for 10 classes, and visiting athletes can drop in for $35.
          </p>
          <p className="mt-4 text-[15px] text-neutral-300 leading-[1.85]">
            Every membership is month-to-month with no contracts. If you're comparing gyms, ask each one what a membership includes —
            then come take a free goal review and see the coaching difference in person.
          </p>
        </div>
      </section>

      {/* Pricing FAQ */}
      <section className="bg-[#0A0A0B] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <FaqList faqs={pricingFaqs} title="Pricing — FAQs" />
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
